// Mints reCAPTCHA Enterprise tokens for the magic-link request.
//
// The site key is public and baked in at build time (Vite define →
// __RECAPTCHA_SITE_KEY__, from VITE_RECAPTCHA_SITE_KEY in Cloud Build);
// Enterprise has no secret key — assessment auth is the backend's IAM.
// enterprise.js loads lazily on the first token request so the marketing
// pages never pay for the script, and every failure path resolves null:
// the backend fails open on a missing token (rate limits backstop), so a
// script blocker or Google outage degrades to "no captcha", never to a
// broken login.
declare const __RECAPTCHA_SITE_KEY__: string | undefined;

const SCRIPT_ID = 'recaptcha-enterprise-js';
const TOKEN_TIMEOUT_MS = 5000;

interface GrecaptchaEnterprise {
  ready(cb: () => void): void;
  execute(siteKey: string, opts: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: GrecaptchaEnterprise };
  }
}

let overrideKey: string | null = null;

// setCaptchaSiteKeyForTests forces the site key ('' disables). Tests only.
export function setCaptchaSiteKeyForTests(key: string | null): void {
  overrideKey = key;
  loading = null;
}

function siteKey(): string {
  if (overrideKey !== null) return overrideKey;
  return typeof __RECAPTCHA_SITE_KEY__ !== 'undefined' ? (__RECAPTCHA_SITE_KEY__ ?? '') : '';
}

let loading: Promise<GrecaptchaEnterprise | null> | null = null;

// loadEnterprise injects enterprise.js once and resolves the grecaptcha
// handle after its async init settles (enterprise.ready) — checking before
// that reads `grecaptcha` undefined and looks like a bug (auth-hardening.md).
function loadEnterprise(key: string): Promise<GrecaptchaEnterprise | null> {
  if (loading) return loading;
  loading = new Promise((resolve) => {
    const settle = (value: GrecaptchaEnterprise | null): void => resolve(value);
    const existing = window.grecaptcha?.enterprise;
    if (existing) {
      existing.ready(() => settle(existing));
      return;
    }
    if (document.getElementById(SCRIPT_ID)) {
      // Script tag present but not settled yet — poll briefly for init.
      const started = Date.now();
      const poll = window.setInterval(() => {
        const g = window.grecaptcha?.enterprise;
        if (g) {
          window.clearInterval(poll);
          g.ready(() => settle(g));
        } else if (Date.now() - started > TOKEN_TIMEOUT_MS) {
          window.clearInterval(poll);
          settle(null);
        }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(key)}`;
    script.async = true;
    script.onload = () => {
      const g = window.grecaptcha?.enterprise;
      if (g) g.ready(() => settle(g));
      else settle(null);
    };
    script.onerror = () => settle(null);
    document.head.appendChild(script);
  });
  return loading;
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([p, new Promise<null>((resolve) => window.setTimeout(() => resolve(null), ms))]);
}

// getCaptchaToken returns a token for `action`, or null when the feature is
// off (no site key) or anything failed — the caller sends the header only
// when a token exists, matching the backend's fail-open policy.
export async function getCaptchaToken(action: string): Promise<string | null> {
  const key = siteKey();
  if (!key) return null;
  try {
    const g = await loadEnterprise(key);
    if (!g) return null;
    const token = await withTimeout(g.execute(key, { action }), TOKEN_TIMEOUT_MS);
    return token || null;
  } catch {
    return null;
  }
}
